import { Injectable, signal, OnDestroy } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import {
  AccountInfo,
  InteractionRequiredAuthError,
  RedirectRequest,
  PublicClientApplication,
  SilentRequest,
} from "@azure/msal-browser";
import { Router } from "@angular/router";
import { ConfigService } from "../config/config.service";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService implements OnDestroy {
  isAuthenticated = signal(false);
  userSignal = signal<AccountInfo | null>(null);
  userRolesSignal = signal<string[]>([]);
  RBACTestDropDownSignal = signal<string[]>([]);
  private loginRequest: RedirectRequest = {
    scopes: ["openid", "profile", "email"], // Fallback scopes, will be overridden by config
  };
  private authChannel: BroadcastChannel | null = null;
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;

  constructor(
    private msalService: MsalService,
    private router: Router,
    private configService: ConfigService
  ) {
    this.setupCrossTabListener();
  }

  async initializeAuth(): Promise<void> {
    // Prevent multiple simultaneous initializations
    if (this.initializationPromise) {
      console.log('Authentication initialization already in progress, waiting for completion...');
      return this.initializationPromise;
    }

    // If already initialized, return immediately
    if (this.isInitialized) {
      console.log('Authentication already initialized, skipping...');
      return Promise.resolve();
    }

    console.log('Starting authentication initialization...');
    // Create and store the initialization promise
    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<void> {
    try {
      // Always call initialize - MSAL handles multiple calls gracefully
      await this.msalService.instance.initialize();
      
      this.msalService.handleRedirectObservable().subscribe(async (result) => {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          const account = accounts[0];
          this.msalService.instance.setActiveAccount(account);
          this.isAuthenticated.set(true);
          this.userSignal.set(account);

        

          const isRBACTestEnabled =
            this.configService.getConfigProperty("RBAC_TEST_ENABLED");
          let roles: string[] = [];

          if (isRBACTestEnabled) {
            const profile = localStorage.getItem("profile");
            if (profile) {
              const profileData = JSON.parse(profile);
              if (profileData.id && profileData.userName === account.username) {
                roles = profileData.roles
                  .split(",")
                  .map((role: string) => role.trim());
              } else {
                roles = account?.idTokenClaims?.roles || [];
              }
            } else {
              roles = account?.idTokenClaims?.roles || [];
            }
          } else {
            roles = account?.idTokenClaims?.roles || [];
          }

          this.RBACTestDropDownSignal.set(account?.idTokenClaims?.roles || []);
          this.setUserRoles(roles);
          
          // Notify other tabs about successful login
          this.notifyOtherTabs('login', account.username);
        } else {
          this.isAuthenticated.set(false);
          this.userSignal.set(null);
          this.setUserRoles([]);
          const postLogoutUrl = this.configService.getConfigProperty('auth')?.postLogoutUrl;
          if (postLogoutUrl) {
            console.log("Redirecting to postLogoutUrl due to token refresh failure");
            window.location.href = postLogoutUrl;
          }
        }
      });
      
      // Mark as initialized after successful setup
      this.isInitialized = true;
      console.log('Authentication initialization completed successfully');
    } catch (err) {
      console.error("MSAL Initialization Error:", err);
      this.isAuthenticated.set(false);
      this.userSignal.set(null);
      this.setUserRoles([]);
      // Navigate to postLogoutUrl for any token refresh errors
      const postLogoutUrl = this.configService.getConfigProperty('auth')?.postLogoutUrl;
      if (postLogoutUrl) {
        console.log("Redirecting to postLogoutUrl due to token refresh failure");
        window.location.href = postLogoutUrl;
      }
      // Reset initialization state on error
      this.isInitialized = false;
      this.initializationPromise = null;
    }
  }

 

  setUserRoles(roles: string[]): void {
    this.userRolesSignal.set(roles);
  }

  logout(): void {
    const profile = localStorage.getItem("profile");
    if (profile) {
      const profileData = JSON.parse(profile);
      profileData.isUpdated = false;
      localStorage.setItem("profile", JSON.stringify(profileData));
    }
    
    // Notify other tabs about logout
    this.notifyOtherTabs('logout');
    
    // Clear active account
    this.msalService.instance.setActiveAccount(null);
    
    // Reset initialization state
    this.isInitialized = false;
    this.initializationPromise = null;
    
    this.performLogoutRedirect();
    this.isAuthenticated.set(false);
    this.userSignal.set(null);
  }

  /**
   * Performs the MSAL logout redirect
   */
  private performLogoutRedirect(): void {
    this.msalService.logoutRedirect();
  }

  getActiveAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  getUserRoles(): string[] {
    const roles = this.userRolesSignal();
    return roles;
  }

  getIsAuthenticated(): boolean {
    return this.isAuthenticated();
  }

  async login(): Promise<void> {
    try {
      // Get scopes from configuration
      const authConfig = this.configService.getConfigProperty('auth');
      const scopes = authConfig?.scopes || this.loginRequest.scopes;
      
      const loginRequest: RedirectRequest = {
        scopes: scopes,
      };
      
      await this.msalService.loginRedirect(loginRequest);
    } catch (err) {
      console.error("Login Error:", err);
    }
  }

  /**
   * Sets up cross-tab communication using BroadcastChannel API
   */
  private setupCrossTabListener(): void {
    try {
      // Check if BroadcastChannel is supported
      if (typeof BroadcastChannel !== 'undefined') {
        this.authChannel = new BroadcastChannel('auth-channel');
        
        this.authChannel.onmessage = (event) => {
          const { type, username } = event.data;
          
          switch (type) {
            case 'logout':
              console.log('Logout detected from another tab');
              this.handleCrossTabLogout();
              break;
            case 'login':
              console.log(`Login detected from another tab for user: ${username}`);
              // Optionally handle login events from other tabs
              break;
            default:
              console.log('Unknown message type received:', type);
          }
        };
      } else {
        console.warn('BroadcastChannel not supported, falling back to window focus detection');
        this.setupFallbackListener();
      }
    } catch (error) {
      console.warn('Failed to setup BroadcastChannel, falling back to window focus detection:', error);
      this.setupFallbackListener();
    }
  }

  /**
   * Fallback listener using window focus events and MSAL account checking
   */
  private setupFallbackListener(): void {
    // Listen for window focus events to check authentication status
    window.addEventListener('focus', () => {
      this.checkAuthenticationStatus();
    });
    
    // Listen for visibility change events
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkAuthenticationStatus();
      }
    });
  }

  /**
   * Check authentication status when switching between tabs
   */
  private checkAuthenticationStatus(): void {
    try {
      // Only check if we're already initialized to avoid triggering re-initialization
      if (!this.isInitialized) {
        return;
      }

      const accounts = this.msalService.instance.getAllAccounts();
      const currentAccount = this.msalService.instance.getActiveAccount();
      
      // If no accounts exist but we think we're authenticated, logout
      if (accounts.length === 0 && this.isAuthenticated()) {
        console.log('No MSAL accounts found, performing logout');
        this.handleCrossTabLogout();
        return;
      }
      
      // If active account doesn't match any available accounts, logout
      if (currentAccount && accounts.length > 0) {
        const accountExists = accounts.some(acc => acc.localAccountId === currentAccount.localAccountId);
        if (!accountExists) {
          console.log('Active account no longer exists, performing logout');
          this.handleCrossTabLogout();
          return;
        }
      }
    } catch (error) {
      console.error('Error checking authentication status:', error);
    }
  }

  /**
   * Handle logout triggered from another tab
   */
  private handleCrossTabLogout(): void {
    // Clear authentication state
    this.isAuthenticated.set(false);
    this.userSignal.set(null);
    
    // Clear active account
    this.msalService.instance.setActiveAccount(null);
    
    // Reset initialization state to allow re-initialization if needed
    this.isInitialized = false;
    this.initializationPromise = null;
    
    // Navigate to logout URL or home page
    const postLogoutUrl = this.configService.getConfigProperty('auth')?.postLogoutUrl || '/';
    this.router.navigate([postLogoutUrl]);
  }

  /**
   * Notify other tabs about authentication events
   */
  private notifyOtherTabs(type: 'login' | 'logout', username?: string): void {
    if (this.authChannel) {
      try {
        this.authChannel.postMessage({ type, username });
      } catch (error) {
        console.warn('Failed to notify other tabs:', error);
      }
    }
  }

  /**
   * Clean up resources when service is destroyed
   */
  ngOnDestroy(): void {
    if (this.authChannel) {
      this.authChannel.close();
      this.authChannel = null;
    }
  }
}
